import { LitElement, html } from 'lit';

import { handleize } from '../utility/strings.js';
import { createRef, ref } from 'lit/directives/ref.js';

import '@decked/decked-design-system/dist/components/button/button.js';
import '@decked/decked-design-system/dist/components/select/select.js';
import '@decked/decked-design-system/dist/components/option/option.js';


class VehiclePicker extends LitElement {

    constructor() {
      super();
    }

    selects = {
      make: createRef(),
      model: createRef(),
      year: createRef(),
    }

    static properties = {
      make: { type: String, state: true },
      makeOptions: { type: Array, state: true },
      model: { type: String, state: true },
      modelOptions: { type: Array, state: true },
      year: { type: String, state: true },
      yearOptions: { type: Array, state: true },

      open: { type: Boolean, state: true },

      filterProducts: { 
        type: Boolean, 
        attribute: 'filter-products',
        reflect: true
      }
    };

    connectedCallback() {
      super.connectedCallback();
      this.open = true;

      if(window.VehicleData !== undefined) {
        if (window._VPV?.vehicleData === undefined) {
          const selectedVehicle = window.VehicleData.init();
          if (selectedVehicle) {
            this.make = selectedVehicle.make;
            this.model = selectedVehicle.model;
            this.year = selectedVehicle.year;
          }
          window.addEventListener('vehicleDataReady', (e) => {
            this.collectVehicleData();
          });
        }else{
          this.collectVehicleData();
        }
      } else {  
        console.error('VehicleData is not defined');
      }
    }

    collectVehicleData() {
      const vehicleData = window.__VPV.vehicleData.byTemplate;
      // Collect all the unique makes
      const makes = new Set();
      const models = new Set();
      const years = new Set();
      for (const vehicle of vehicleData) {
        makes.add(vehicle.make);
        if(this.make == vehicle.make) {
          models.add(vehicle.model);
        }
        if(this.model == vehicle.model && this.make == vehicle.make) {
          years.add(vehicle.year);
        }
      }
     
      this.makeOptions = Array.from(makes).map(make => ({ value: handleize(make), label: make })).sort(this.sortMakeOptions);
      this.modelOptions = Array.from(models).map(model => ({ value: handleize(model), label: model })).sort(this.sortModelOptions);
      this.yearOptions = Array.from(years).map(year => ({ value: handleize(year), label: year })).sort((a, b) => b.label - a.label); 
    }

    clearVehicle() {
      this.make = '';
      this.model = '';
      this.year = '';
      this.open = true;
      this.unFilterProducts();
    }

    selectVehicle() {
      if (!this.make || this.make == '' || !this.model || this.model == '' || !this.year || this.year == '') {
        return;
      }
      const vehicleData = window.__VPV.vehicleData.byTemplate;
      const selectedVehicle = vehicleData.find(vehicle => {
        return vehicle.make == this.make && vehicle.model == this.model && vehicle.year == this.year;
      });
      if (selectedVehicle) {
        window.VehicleData.selectVehicle(selectedVehicle);
      }
      this.open = false;
      if (this.filterProductsByVehicle) {
        this.filterProductsByVehicle();
      }
    }

    unFilterProducts = () => {
      const productCards = document.querySelectorAll('product-card');
      productCards.forEach((card) => {
        card.classList.remove('product-card--compatible');
        card.classList.remove('product-card--not-compatible');
      });
    }

    filterProductsByVehicle = () => {
      const vehicleData = window.__VPV.vehicleData.bySelection;
      const validProductHandles = new Set();
      for (const vehicle of vehicleData) {
        if (vehicle.make == this.make && vehicle.model == this.model && vehicle.year == this.year) {
          for (const product of vehicle.products) {
            validProductHandles.add(product.productHandle);
          }
        }
      }
      const productCards = document.querySelectorAll('product-card');
      productCards.forEach((card) => {
        const productHandle = card.getAttribute('handle');
        if (validProductHandles.has(productHandle) || !productHandle) {
          card.classList.add('product-card--compatible');
          card.classList.remove('product-card--not-compatible');
        } else {
          card.classList.add('product-card--not-compatible');
          card.classList.remove('product-card--compatible');
        }
      });
    }

    selectOption = (attribute) => (e) => {
      const value = e.target.value;
      switch (attribute) {
        case 'make':
          this.make = this.makeOptions.find(({value}) => e.target.value == value).label;
          this.model = '';
          this.year = '';
          break;
        case 'model':
          this.model = this.modelOptions.find(({value}) => e.target.value == value).label;
          this.year = '';
          break;
        case 'year':
          this.year = this.yearOptions.find(({value}) => e.target.value == value).label;
          break;
      }
      this.collectVehicleData();
      this.openNextSelect();
    }

    openNextSelect() {
      if (!this.make || this.make == '') {
        this.selects.make.value.show();
      } else if (!this.model || this.model == '') {
        this.selects.model.value.show();
      } else if (!this.year || this.year == '') {
        this.selects.year.value.show();
      }
    }

    sortMakeOptions(a, b) {
      // in reverse order so anything not in the list drops to the end
      const order = ["Mercedes Benz (all Sprinters)", "Sprinter Van", "Jeep Trucks", "Jeep", "Nissan", "GMC", "Toyota", "Ram",  "Chevy", "Ford"];

      const indexA = order.indexOf(a.label);
      const indexB = order.indexOf(b.label);
      if (indexA < indexB) {
        return 1;
      } else if (indexA > indexB) {
        return -1;
      } else {
        return 0;
      }
    }

    sortModelOptions(a, b) {
      const containsVanOrService = option => option.label?.includes("Van") || option.label?.includes("Service");

      if (containsVanOrService(a) && !containsVanOrService(b)) {
        return 1;
      } else if (!containsVanOrService(a) && containsVanOrService(b)) {
        return -1;
      } else {
        return a.label.localeCompare(b.label);
      }
    }

    render() {
      
      const makeOptions = this.makeOptions?.map(option => html`
        <sl-option value="${option.value}">${option.label}</sl-option>
      `);
      const modelOptions = this.modelOptions?.length ? this.modelOptions.map(option => html`
        <sl-option value="${option.value}">${option.label}</sl-option>
      `) : html`<sl-option value="please-select" disabled>Please Select Make...</sl-option>`;
      const yearOptions = this.yearOptions?.length ? this.yearOptions.map(option => html`
        <sl-option value="${option.value}">${option.label}</sl-option>
      `) : html`<sl-option value="please-select" disabled>Please Select Make...</sl-option>`;

      const makeValue = this.makeOptions?.find(({label}) => this.make == label)?.value;
      const modelValue = this.modelOptions?.find(({label}) => this.model == label)?.value;
      const yearValue = this.yearOptions?.find(({label}) => this.year == label)?.value;

      return html`
        <form>
          ${ this.open ? html`
            <div class="flex flex-col sm:flex-row gap-2 items-center justify-center">
              <sl-select class="w-full sm:w-auto" ${ref(this.selects.make)} value=${makeValue} placeholder="Make..." size="large" @sl-change=${this.selectOption('make')}>
                ${makeOptions}
              </sl-select>
              <sl-select class="w-full sm:w-auto" ${ref(this.selects.model)} value=${modelValue} placeholder="Model..." size="large" @sl-change=${this.selectOption('model')}>
                ${modelOptions}
              </sl-select>
              <sl-select class="w-full sm:w-auto" ${ref(this.selects.year)} value=${yearValue} placeholder="Year..." size="large" @sl-change=${this.selectOption('year')}>
                ${yearOptions}
              </sl-select>
              <sl-button class="w-full sm:w-auto" variant="primary" size="large" @click=${this.selectVehicle}>Select Vehicle</sl-button>
            </div>` : html`
            <div class="border flex bg-lightest-grey rounded-lg p-4 justify-between items-center max-w-xl mx-auto">
              <div>${this.make} ${this.model} ${this.year}</div>
              <button class="link" type='reset' @click=${this.clearVehicle}>Change</button>
            </div>`}
        </form>
      `;
    }

    createRenderRoot() {
      return this;
    }

}
if (!window.customElements.get("decked-vehicle-picker")) {
  customElements.define('decked-vehicle-picker', VehiclePicker);
}